import React, { useEffect, useRef, useState } from "react";
import { useProposal } from "../../context/ProposalContext";
import { useAuth } from "../../context/AuthContext";
import DocumentUpload from "./DocumentUpload";
import FirstNationSelect from "./FirstNationSelect";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { ArrowLeft, ArrowRight, ShieldCheck, Info } from "lucide-react";

const PROVINCES = [
  "Alberta",
  "British Columbia",
  "Manitoba",
  "New Brunswick",
  "Newfoundland and Labrador",
  "Northwest Territories",
  "Nova Scotia",
  "Nunavut",
  "Ontario",
  "Prince Edward Island",
  "Quebec",
  "Saskatchewan",
  "Yukon",
];

const INCOMPLETE_MESSAGE =
  "We are happy to provide you with this free proposal. Please complete this section, so that we are able to send it to you right away. Thank you!";

const validateLead = (lead, consent) => {
  const e = {};
  if (!lead.name?.trim()) e.name = "Full name is required.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(lead.email || "")) e.email = "Enter a valid email.";
  if (!lead.jobTitle?.trim()) e.jobTitle = "Job position or title is required.";
  if (!lead.firstNation?.trim()) e.firstNation = "Name of First Nation is required.";
  if (!lead.street?.trim()) e.street = "Street / PO Box is required.";
  if (!lead.city?.trim()) e.city = "City is required.";
  if (!lead.province) e.province = "Province is required.";
  if (!lead.postalCode?.trim()) e.postalCode = "Postal code is required.";
  if (!lead.phone?.trim()) e.phone = "Phone number is required.";
  if (!consent) e.consent = "Please acknowledge the conditions of use.";
  return e;
};

export default function Step3RecipientInfo({ onNext, onBack }) {
  const { state, dispatch } = useProposal();
  const { user } = useAuth();
  const isStaff = !!user && user !== false;
  const lead = state.lead;
  const [errors, setErrors] = useState({});
  const [consent, setConsent] = useState(!!lead.consentAccepted);
  const [showPopup, setShowPopup] = useState(false);
  const popupTimer = useRef(null);

  useEffect(() => {
    const timer = popupTimer;
    return () => clearTimeout(timer.current);
  }, []);

  const set = (k, v) => dispatch({ type: "UPDATE_LEAD", payload: { [k]: v } });

  const triggerPopup = () => {
    clearTimeout(popupTimer.current);
    setShowPopup(true);
    popupTimer.current = setTimeout(() => setShowPopup(false), 10000);
  };

  const handleContinue = () => {
    const e = validateLead(lead, consent);
    setErrors(e);
    if (Object.keys(e).length > 0) {
      triggerPopup();
      return;
    }
    dispatch({ type: "UPDATE_LEAD", payload: { organization: lead.firstNation, consentAccepted: true } });
    onNext();
  };

  const err = (k) =>
    errors[k] ? (
      <p data-testid={`recipient-error-${k}`} className="text-xs text-destructive">{errors[k]}</p>
    ) : null;

  return (
    <div className="space-y-6" data-testid="recipient-info-step">
      <div>
        <h2 className="font-display font-700 text-2xl text-foreground">Recipient Information</h2>
        <p className="text-muted-foreground text-sm mt-1">
          Tell us where to send the proposal. All fields are required so we can deliver it right away.
        </p>
      </div>

      {showPopup && (
        <div
          data-testid="incomplete-popup"
          className="rounded-xl border border-primary/40 bg-primary text-white p-4 flex items-start gap-3 shadow-md"
        >
          <Info className="h-5 w-5 shrink-0 mt-0.5" />
          <p className="text-sm leading-relaxed">{INCOMPLETE_MESSAGE}</p>
        </div>
      )}

      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="r-name">Full Name</Label>
          <Input id="r-name" data-testid="lead-name-input" value={lead.name || ""}
            onChange={(e) => set("name", e.target.value)} placeholder="Jane Doe" />
          {err("name")}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="r-email">Email Address</Label>
            <Input id="r-email" data-testid="lead-email-input" type="email" value={lead.email || ""}
              onChange={(e) => set("email", e.target.value)} placeholder="jane@nation.ca" />
            {err("email")}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="r-job">Job Position / Title</Label>
            <Input id="r-job" data-testid="lead-jobTitle-input" value={lead.jobTitle || ""}
              onChange={(e) => set("jobTitle", e.target.value)} placeholder="Election Officer" />
            {err("jobTitle")}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="r-fn">Name of First Nation</Label>
          <FirstNationSelect
            value={lead.firstNation || ""}
            onChange={(sel) => {
              dispatch({
                type: "UPDATE_LEAD",
                payload: { firstNation: sel?.name || "", firstNationId: sel?.id || "" },
              });
              if (sel?.name) setErrors((p) => ({ ...p, firstNation: undefined }));
            }}
            invalid={!!errors.firstNation}
          />
          {err("firstNation")}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="r-street">Mailing Address — Street / PO Box</Label>
          <Input id="r-street" data-testid="lead-street-input" value={lead.street || ""}
            onChange={(e) => set("street", e.target.value)} placeholder="123 Main St / PO Box 100" />
          {err("street")}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="r-city">City</Label>
            <Input id="r-city" data-testid="lead-city-input" value={lead.city || ""}
              onChange={(e) => set("city", e.target.value)} placeholder="Vancouver" />
            {err("city")}
          </div>
          <div className="space-y-1.5">
            <Label>Province</Label>
            <Select value={lead.province || ""} onValueChange={(v) => set("province", v)}>
              <SelectTrigger data-testid="lead-province-select"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {PROVINCES.map((p) => (
                  <SelectItem key={p} value={p}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {err("province")}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="r-postal">Postal Code</Label>
            <Input id="r-postal" data-testid="lead-postalCode-input" value={lead.postalCode || ""}
              onChange={(e) => set("postalCode", e.target.value)} placeholder="V5K 0A1" />
            {err("postalCode")}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="r-phone">Phone Number</Label>
          <Input id="r-phone" data-testid="lead-phone-input" type="tel" value={lead.phone || ""}
            onChange={(e) => set("phone", e.target.value)} placeholder="(604) 555-0123" />
          {err("phone")}
        </div>

        {/* Internal-only: shown to logged-in SmartBallot staff, hidden on the public wizard */}
        {isStaff && (
          <div data-testid="prepared-by-section" className="rounded-xl border border-primary/30 bg-accent/40 p-4 space-y-1.5">
            <Label htmlFor="r-preparedby">Prepared by (your name)</Label>
            <Input
              id="r-preparedby"
              data-testid="prepared-by-input"
              value={state.event.preparedBy || ""}
              onChange={(e) => dispatch({ type: "UPDATE_EVENT", payload: { preparedBy: e.target.value } })}
              placeholder="SmartBallot staff name"
            />
            <p className="text-xs text-muted-foreground">
              Internal only — recorded with the proposal for your team. Not shown to the recipient.
            </p>
          </div>
        )}

        <DocumentUpload
          document={state.document}
          onChange={(doc) => dispatch({ type: "SET_DOCUMENT", document: doc })}
        />

        {/* AI use & privacy acknowledgment */}
        <div data-testid="ai-consent-box" className="rounded-xl border border-primary/30 bg-accent/50 p-4">
          <div className="flex items-center gap-2 text-primary mb-2">
            <ShieldCheck className="h-4 w-4" />
            <p className="font-display font-600 text-sm">Generated with an AI assistant</p>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            This proposal is being generated using an AI assistant. The information you provide here
            is collected for <strong>one-time use only</strong> and will be <strong>deleted once the
            proposal is completed</strong>. It will <strong>NOT</strong> be used to train or inform any
            machine learning model, large language model, or external database.
          </p>
          <label className="flex items-start gap-2.5 mt-3 cursor-pointer">
            <Checkbox
              data-testid="consent-checkbox"
              checked={consent}
              onCheckedChange={(v) => {
                setConsent(!!v);
                dispatch({ type: "UPDATE_LEAD", payload: { consentAccepted: !!v } });
                setErrors((p) => ({ ...p, consent: undefined }));
              }}
              className="mt-0.5"
            />
            <span className="text-xs text-foreground">
              I acknowledge and accept these conditions of use.
            </span>
          </label>
          {err("consent")}
        </div>
      </div>

      <div className="flex justify-between pt-2">
        <Button variant="outline" data-testid="back-to-estimate-btn" onClick={onBack} className="gap-2">
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <Button
          data-testid="continue-to-proposal-btn"
          onClick={handleContinue}
          className="gap-2 transition-transform hover:-translate-y-0.5 hover:shadow-md"
        >
          Generate Proposal <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
